'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  CarFront,
  ChevronRight,
  Home,
  LayoutDashboard,
  MessageSquare,
  MoreHorizontal,
  Package,
  Plus,
  Search,
  ShieldCheck,
  ShoppingBag,
  Tag,
  User,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/context/LanguageContext';
import {
  getDashboardPathForRole,
  getGuestSession,
  getVendorWorkspacePath,
  isAdminRole,
  isVendorRole,
  readStoredSession,
} from '@/lib/auth/session';

type NavItem = {
  icon: LucideIcon;
  labelAr: string;
  labelEn: string;
  href?: string;
  matchPrefix?: string;
  action?: 'more';
};

type SheetAction = {
  icon: LucideIcon;
  labelAr: string;
  labelEn: string;
  href: string;
};

export default function BottomNav() {
  const pathname = usePathname();
  const { lang } = useLanguage();
  const [session, setSession] = useState(() => getGuestSession());
  const [activeSheet, setActiveSheet] = useState<'none' | 'add' | 'more'>('none');
  const [isPhoneDevice, setIsPhoneDevice] = useState(false);

  useEffect(() => {
    const syncAuth = () => setSession(readStoredSession());
    syncAuth();
    window.addEventListener('storage', syncAuth);
    window.addEventListener('s360-auth-changed', syncAuth);
    return () => {
      window.removeEventListener('storage', syncAuth);
      window.removeEventListener('s360-auth-changed', syncAuth);
    };
  }, []);

  useEffect(() => {
    const detectPhoneLikeDevice = () => {
      const forcedMobile = new URLSearchParams(window.location.search).get('mobile_nav') === '1';
      const ua = navigator.userAgent || '';
      const mobileHint = Boolean((navigator as any).userAgentData?.mobile);
      const isIpad = /iPad|Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
      const isPhoneUA = /Android.+Mobile|iPhone|iPod|Windows Phone|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
      const smallViewport = window.matchMedia('(max-width: 767px)').matches;
      const hasTouchLikeInput =
        window.matchMedia('(hover: none)').matches ||
        window.matchMedia('(pointer: coarse)').matches ||
        navigator.maxTouchPoints > 0 ||
        'ontouchstart' in window;
      const devtoolsEmulationViewport =
        smallViewport && Math.max(0, window.outerWidth - window.innerWidth) > 300;
      const emulatedPhoneViewport = smallViewport && (hasTouchLikeInput || devtoolsEmulationViewport);
      setIsPhoneDevice(forcedMobile || ((mobileHint || isPhoneUA || emulatedPhoneViewport) && !isIpad));
    };

    detectPhoneLikeDevice();
    window.addEventListener('resize', detectPhoneLikeDevice);
    return () => {
      window.removeEventListener('resize', detectPhoneLikeDevice);
    };
  }, []);

  useEffect(() => {
    setActiveSheet('none');
  }, [pathname]);

  useEffect(() => {
    if (activeSheet === 'none') return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [activeSheet]);

  const hiddenOnRoutes = new Set([
    '/admin',
    '/vendor',
    '/showroom',
    '/login',
    '/register',
    '/showroom/register',
    '/public-register',
    '/reset-password',
    '/forgot-password',
  ]);

  if (hiddenOnRoutes.has(pathname)) {
    return null;
  }

  if (!isPhoneDevice) {
    return null;
  }

  const role = session.user?.role as string | undefined;
  const dashboardHref = getDashboardPathForRole(role);
  const vendorWorkspaceHref = getVendorWorkspacePath(session.user);
  const vendorIsShowroom = vendorWorkspaceHref === '/showroom';
  const admin = isAdminRole(role);
  const vendor = isVendorRole(role);

  const t = (ar: string, en: string) => (lang === 'en' ? en : ar);

  const navItems: NavItem[] = vendor
    ? [
        { icon: Home, labelAr: 'الرئيسية', labelEn: 'Home', href: '/' },
        {
          icon: ShoppingBag,
          labelAr: 'السوق',
          labelEn: 'Market',
          href: '/marketplace',
          matchPrefix: '/marketplace',
        },
        vendorIsShowroom
          ? {
              icon: CarFront,
              labelAr: 'السيارات',
              labelEn: 'Cars',
              href: '/showroom',
              matchPrefix: '/showroom',
            }
          : {
              icon: Package,
              labelAr: 'المخزون',
              labelEn: 'Inventory',
              href: '/vendor/inventory',
              matchPrefix: '/vendor',
            },
        { icon: MoreHorizontal, labelAr: 'المزيد', labelEn: 'More', action: 'more' },
      ]
    : admin
      ? [
          { icon: Home, labelAr: 'الرئيسية', labelEn: 'Home', href: '/' },
          {
            icon: ShoppingBag,
            labelAr: 'السوق',
            labelEn: 'Market',
            href: '/marketplace',
            matchPrefix: '/marketplace',
          },
          {
            icon: ShieldCheck,
            labelAr: 'الإدارة',
            labelEn: 'Admin',
            href: '/admin',
            matchPrefix: '/admin',
          },
          { icon: MoreHorizontal, labelAr: 'المزيد', labelEn: 'More', action: 'more' },
        ]
      : [
          { icon: Home, labelAr: 'الرئيسية', labelEn: 'Home', href: '/' },
          { icon: Search, labelAr: 'السيارات', labelEn: 'Cars', href: '/buy', matchPrefix: '/buy' },
          {
            icon: ShoppingBag,
            labelAr: 'السوق',
            labelEn: 'Market',
            href: '/marketplace',
            matchPrefix: '/marketplace',
          },
          { icon: MoreHorizontal, labelAr: 'المزيد', labelEn: 'More', action: 'more' },
        ];

  const addActions: SheetAction[] = [
    { icon: CarFront, labelAr: 'إضافة سيارة', labelEn: 'Add Car Listing', href: '/sell' },
    {
      icon: Tag,
      labelAr: 'إضافة عنصر للسوق',
      labelEn: 'Add Marketplace Item',
      href: '/marketplace/new',
    },
    { icon: Wrench, labelAr: 'الخدمات المتكاملة', labelEn: 'Services Hub', href: '/services' },
  ];

  const moreActions: SheetAction[] = vendor
    ? [
        { icon: Search, labelAr: 'شراء سيارة', labelEn: 'Buy Car', href: '/buy' },
        { icon: Package, labelAr: 'قطع الغيار', labelEn: 'Spare Parts', href: '/parts' },
        { icon: Wrench, labelAr: 'الورش والصيانة', labelEn: 'Workshops', href: '/maintenance' },
        { icon: LayoutDashboard, labelAr: 'لوحتي', labelEn: 'Dashboard', href: vendorWorkspaceHref },
        { icon: User, labelAr: 'الملف الشخصي', labelEn: 'Profile', href: dashboardHref },
        { icon: MessageSquare, labelAr: 'المحادثات', labelEn: 'Chat', href: '/chat' },
      ]
    : admin
      ? [
          { icon: Search, labelAr: 'شراء سيارة', labelEn: 'Buy Car', href: '/buy' },
          { icon: Package, labelAr: 'قطع الغيار', labelEn: 'Spare Parts', href: '/parts' },
          { icon: Wrench, labelAr: 'الخدمات المتكاملة', labelEn: 'Services', href: '/services' },
          { icon: ShieldCheck, labelAr: 'لوحة الإدارة', labelEn: 'Admin Panel', href: '/admin' },
          { icon: User, labelAr: 'الملف الشخصي', labelEn: 'Profile', href: dashboardHref },
          { icon: MessageSquare, labelAr: 'المحادثات', labelEn: 'Chat', href: '/chat' },
        ]
      : [
          { icon: Search, labelAr: 'شراء سيارة', labelEn: 'Buy Car', href: '/buy' },
          { icon: Package, labelAr: 'قطع الغيار', labelEn: 'Spare Parts', href: '/parts' },
          { icon: Wrench, labelAr: 'الورش والصيانة', labelEn: 'Workshops', href: '/maintenance' },
          { icon: LayoutDashboard, labelAr: 'لوحتي', labelEn: 'Dashboard', href: dashboardHref },
          { icon: User, labelAr: 'الملف الشخصي', labelEn: 'Profile', href: '/profile' },
          { icon: MessageSquare, labelAr: 'المحادثات', labelEn: 'Chat', href: '/chat' },
        ];

  const renderTab = (item: NavItem) => {
    const active =
      item.action === 'more'
        ? activeSheet === 'more'
        : item.matchPrefix
          ? pathname.startsWith(item.matchPrefix)
          : pathname === item.href;

    const iconNode = (
      <div
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-[10px] transition-colors',
          active
            ? 'bg-primary/15 text-[#1E5CC1] dark:bg-blue-500/15 dark:text-blue-300'
            : 'text-slate-500 dark:text-slate-400'
        )}
      >
        <item.icon className="h-[19px] w-[19px]" />
      </div>
    );

    const labelNode = (
      <span
        className={cn(
          'text-[11px] leading-none transition-colors',
          active
            ? 'font-bold text-[#1E5CC1] dark:text-blue-300'
            : 'font-medium text-slate-500 dark:text-slate-400'
        )}
      >
        {t(item.labelAr, item.labelEn)}
      </span>
    );

    if (item.action === 'more') {
      return (
        <button
          key={`${item.labelAr}-action`}
          type="button"
          onClick={() => setActiveSheet('more')}
          className="flex flex-1 flex-col items-center justify-center gap-1"
          aria-label={t(item.labelAr, item.labelEn)}
        >
          {iconNode}
          {labelNode}
        </button>
      );
    }

    return (
      <Link
        key={item.href}
        href={item.href || '/'}
        className="flex flex-1 flex-col items-center justify-center gap-1"
        aria-label={t(item.labelAr, item.labelEn)}
      >
        {iconNode}
        {labelNode}
      </Link>
    );
  };

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-50 px-3 pb-safe md:hidden">
        <nav className="mx-auto w-full max-w-md rounded-[26px] border border-slate-200/95 bg-white/95 shadow-[0_10px_28px_rgba(15,23,42,0.14)] backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 dark:shadow-[0_16px_30px_rgba(2,6,23,0.5)]">
          <div className="flex h-[74px] items-center px-2">
            {renderTab(navItems[0])}
            {renderTab(navItems[1])}

            <div className="flex flex-1 items-center justify-center">
              <button
                type="button"
                onClick={() => setActiveSheet('add')}
                className="relative -mt-8 flex h-[66px] w-[66px] items-center justify-center rounded-full border-[3px] border-[#DCE8FF] bg-gradient-to-br from-[#2A6DD6] to-[#1E5CC1] text-white shadow-[0_10px_20px_rgba(30,92,193,0.35)] transition-transform active:scale-95 dark:border-[#27467A]"
                aria-label={t('إضافة', 'Add')}
              >
                <Plus className="h-7 w-7" />
              </button>
            </div>

            {renderTab(navItems[2])}
            {renderTab(navItems[3])}
          </div>
        </nav>
      </div>

      {activeSheet !== 'none' && (
        <div className="fixed inset-0 z-[70] md:hidden">
          <button
            type="button"
            aria-label={t('إغلاق', 'Close')}
            className="absolute inset-0 bg-slate-950/35 backdrop-blur-[1px]"
            onClick={() => setActiveSheet('none')}
          />

          <div className="absolute inset-x-0 bottom-0 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
            <div className="mx-auto w-full max-w-md animate-in slide-in-from-bottom-4 rounded-[20px] border border-slate-200 bg-white pb-2 shadow-[0_20px_40px_rgba(2,6,23,0.3)] duration-200 dark:border-slate-800 dark:bg-slate-900">
              <div className="flex justify-center py-2.5">
                <div className="h-1 w-10 rounded-full bg-slate-300 dark:bg-slate-700" />
              </div>

              <div className="px-2">
                {(activeSheet === 'add' ? addActions : moreActions).map((item) => (
                  <Link
                    key={`${activeSheet}-${item.href}-${item.labelAr}`}
                    href={item.href}
                    className="flex items-center gap-3 rounded-2xl px-3 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                    onClick={() => setActiveSheet('none')}
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <item.icon className="h-4 w-4" />
                    </div>
                    <span className="flex-1">{t(item.labelAr, item.labelEn)}</span>
                    <ChevronRight className={cn('h-4 w-4 text-slate-400', lang === 'ar' && 'rtl-flip')} />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
